import { observer } from 'mobx-react-lite'
import { compact } from 'lodash'
import { Sparks, User, UserPlus } from 'iconoir-react'
import type { ReactElement, ReactNode } from 'react'
import Button from 'app/components/Button'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import ExportRows from 'app/components/table/ExportRows'
import TableLayout from 'app/components/table/TableLayout'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import type { FixedRules } from 'app/types/filters'
import { useContactBulkActions } from 'app/components/contacts/useContactBulkActions'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { useListCopy } from 'app/hooks/useListCopy'
import { FilterObject } from 'app/filter.utils'
import type { List } from 'app/generated/graphql'
import type { UseContactsPageReturn } from './useContactsPage'

export type ContactsPageComponentProps = UseContactsPageReturn & {
  viewToggle?: ReactElement
  titleActions?: ReactElement
  /**
   * Whether the header offers New Contact. Off on list pages: a contact made there would
   * land wherever the filter or the manual membership says, not in the list on screen.
   */
  newContact?: boolean
  /** The manual list whose members the table shows, so the bulk bar offers Remove from list for it. */
  removeFromList?: List
  /** Drawn right after the page title, such as a personal list's lock. */
  titleMarker?: ReactNode
  /** A quiet line under the title row, such as a list's kind. */
  subheader?: ReactNode
  fixedRules?: FixedRules
}

export const ContactsPageComponent = observer(function ContactsPageComponent(props: ContactsPageComponentProps) {
  const {
    data,
    user,
    contacts,
    contactsTotalItems,
    focusedContact,
    isSwitchingContact,
    openContactDrawer,
    drawerTab,
    memoizedFilters,
    columns,
    initialSort,
    contactTraversal,
    workspace,
    contactHandler,
    closeDrawerHandler,
    loadContact,
    addContact,
    loadMore,
    searchTerms,
    viewToggle,
    titleActions,
    newContact = true,
    removeFromList,
    fixedRules,
    titleMarker,
    subheader,
  } = props

  const bulkActions = useContactBulkActions({ removeFromList })
  const isMobile = useIsMobile()
  const copy = useListCopy()

  if (!user) return null

  return (
    <PageScroll
      variant="table"
      header={
        <PageHeader
          title={data.activeSubpage === `All` ? `Contacts` : data.activeSubpage}
          titleMarker={titleMarker}
          subheader={subheader}
          totalItems={contactsTotalItems}
          otherButtons={compact([
            titleActions,
            workspace != null && newContact && (
              <Button
                key="new-contact"
                mode={isMobile ? `tertiary` : `primary`}
                size={isMobile ? `sm` : undefined}
                onClick={() => contactHandler(`new`)}
                aria-label="New Contact"
              >
                <UserPlus className={isMobile ? `` : `mr-1`} /> {!isMobile && `New Contact`}
              </Button>
            ),
          ])}
        />
      }
    >
      <TableLayout
        key={data.slug}
        filterableColumns={memoizedFilters}
        fixedRules={fixedRules}
        defaultJoin={data.joiner}
        data={contacts}
        columns={columns as any}
        loadMore={loadMore}
        initialSort={initialSort}
        initialFilters={data.initialFilters as FilterObject[]}
        // The list's view switch ends the toolbar row, where the board draws
        // them too, so they stay put across views.
        trailingControls={viewToggle}
        // A list page's Settings carries column lines and columns, so the toolbar drops both.
        viewToolsInSettings={titleActions !== undefined}
        bulkActions={bulkActions}
        extraButtons={(cols, columnVisibility) => [
          <ExportRows
            key={1}
            loaderPropertyName={`contacts`}
            totalSize={contacts?.total ?? 0}
            columns={cols}
            columnVisibility={columnVisibility}
            search={searchTerms}
          />,
        ]}
        meta={{
          getRowStyle: (row) => (row?.id === focusedContact?.id ? `table-selected-row` : ``),
        }}
        edgePadded
        emptyState={
          // Active Leads is a work queue, so an empty one means the team has
          // worked every lead rather than that something is missing. Say so.
          data.slug === `active-leads` ? (
            <TableEmptyState
              tone="positive"
              icon={<Sparks className="h-6 w-6" strokeWidth={1.5} />}
              title="You are all caught up"
              description="Every active lead has been worked. New leads land here the moment they come in, so there is nothing waiting on your team right now."
            />
          ) : data.slug === `all` ? (
            <TableEmptyState
              icon={<User className="h-6 w-6" strokeWidth={1.5} />}
              title="No contacts yet"
              description={copy.allContactsEmptyDescription}
            />
          ) : (
            // Every other list carries filters of its own, so an empty one says
            // nothing about whether the practice has contacts. "All" is the only
            // list where empty really does mean the database is empty.
            <TableEmptyState
              icon={<User className="h-6 w-6" strokeWidth={1.5} />}
              title="Nothing in this list"
              description="Contacts show up here on their own as soon as they match this list. None of them do right now."
            />
          )
        }
      />

      <ContactDrawer
        openDrawer={openContactDrawer}
        contact={focusedContact}
        onClose={closeDrawerHandler}
        initialTab={drawerTab}
        onChange={loadContact}
        nextContact={contactTraversal.next}
        previousContact={contactTraversal.prev}
        isNavigating={contactTraversal.isNavigating || isSwitchingContact}
      />
    </PageScroll>
  )
})
